import React, { useState } from 'react';
import { awsService } from '@/services/api/awsService';
import { useAuthStore } from '@/stores/authStore';

function UserEnableForm() {
  const { currentUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [confirmUsername, setConfirmUsername] = useState('');
  const [srTicket, setSrTicket] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [executionStatus, setExecutionStatus] = useState('');
  const [executionResults, setExecutionResults] = useState(null);

  const pollExecutionResult = async (executionId, maxAttempts = 15, interval = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await awsService.getExecutionResult(executionId);
        
        setExecutionStatus(`Checking execution status... (${attempt + 1}/${maxAttempts})`);
        
        if (result['execution-error-message']) {
          const errorMsg = result['execution-error-message'];
          if (errorMsg.includes('NoSuchKey') || errorMsg.includes('does not exist')) {
            setExecutionStatus(`Waiting for results file... (${attempt + 1}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, interval));
            continue;
          }
          return { success: false, error: errorMsg };
        }
        
        if (result['execution-status-code'] === 200) {
          return { success: true, result };
        } else if (result['execution-status-code'] === 202) {
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        } else {
          return { 
            success: false, 
            error: result['execution-error-message'] || 'Execution failed' 
          };
        }
      } catch (error) {
        const errorMessage = error.message || error.toString();
        if (errorMessage.includes('NoSuchKey') || errorMessage.includes('does not exist')) {
          setExecutionStatus(`Waiting for results file... (${attempt + 1}/${maxAttempts})`);
          await new Promise(resolve => setTimeout(resolve, interval));
          continue;
        }
        if (attempt === maxAttempts - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    throw new Error('Execution timeout: The operation is taking longer than expected');
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setMessage({ type: 'error', text: 'Please enter a search term.' });
      return;
    }

    setSearching(true);
    setMessage({ type: '', text: '' });
    setSearchResults([]);
    setSelectedUser(null);
    setConfirmUsername('');

    try {
      const results = await awsService.searchADUsers(searchQuery);
      
      if (results && results.length > 0) {
        setSearchResults(results);
        setMessage({ type: 'success', text: `Found ${results.length} user(s).` });
      } else {
        setSearchResults([]);
        setMessage({ type: 'info', text: 'No users found matching your search.' });
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setMessage({ type: 'error', text: 'Error searching users. Please try again.' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    if (user.Enabled) {
      setMessage({ type: 'error', text: 'This user is already enabled.' });
      return;
    }
    
    setSelectedUser({
      username: user.SamAccountName || '',
      email: user.EmailAddress || '',
      firstName: user.FirstName || '',
      lastName: user.LastName || '',
      company: user.Description || '',
      enabled: user.Enabled
    });
    setSearchResults([]);
    setMessage({ type: '', text: '' });
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    
    if (confirmUsername !== selectedUser?.username) {
      setMessage({ 
        type: 'error', 
        text: 'Username confirmation does not match. Please type the exact username to confirm.' 
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    setExecutionStatus('Submitting user enable request...');

    try {
      const requestBody = {
        UsersToEnable: [
          {
            user: selectedUser.username,
            sr: srTicket || ''
          }
        ],
        RequestedBy: currentUser?.email || 'unknown@lla.com'
      };

      const response = await awsService.enableADUsers(requestBody);
      
      if (!response['execution-id']) {
        throw new Error('No execution ID received from server');
      }

      setExecutionStatus('User enable initiated. Waiting for completion...');
      
      const pollResult = await pollExecutionResult(response['execution-id']);
      
      if (pollResult.success) {
        const result = pollResult.result;
        if (result['execution-result']) {
          setExecutionResults(result['execution-result']);
        }
        setMessage({ type: 'success', text: 'User enabled successfully in Active Directory!' });
        setSelectedUser(null);
        setConfirmUsername('');
        setSrTicket('');
      } else {
        throw new Error(pollResult.error);
      }
    } catch (error) {
      console.error('Error enabling user:', error);
      setMessage({ type: 'error', text: error.message || 'Error enabling user. Please try again.' });
    } finally {
      setLoading(false);
      setExecutionStatus('');
    }
  };

  const renderExecutionResults = () => {
    if (!executionResults) return null;

    if (Array.isArray(executionResults)) {
      return (
        <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Execution Details:</h4>
          <div className="space-y-2">
            {executionResults.map((item, index) => (
              <div key={index} className="bg-white rounded p-3 border border-gray-200">
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                    item.SuccessfulAction ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {item.SuccessfulAction ? (
                      <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-900 mb-1">{item.ActionName}</div>
                    <div className={`text-xs ${item.SuccessfulAction ? 'text-gray-700' : 'text-red-700'}`}>
                      {item.ActionMessage}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Execution Details:</h4>
        <pre className="text-xs text-gray-700 overflow-auto max-h-60">
          {JSON.stringify(executionResults, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
          Search User
        </h3>
        
        <form onSubmit={handleSearch} className="flex space-x-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Search by username or email"
            disabled={searching}
          />
          <button
            type="submit"
            disabled={searching}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </form>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-700">Select a user to enable:</p>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {searchResults.map((user, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectUser(user)}
                  className="w-full px-4 py-3 text-left hover:bg-green-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.ObjectName}</p>
                      <p className="text-xs text-gray-600">{user.SamAccountName} • {user.EmailAddress}</p>
                      {user.Description && (
                        <p className="text-xs text-gray-500 mt-1">Company: {user.Description}</p>
                      )}
                      <p className="text-xs mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          user.Enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.Enabled ? 'Active' : 'Disabled'}
                        </span>
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {message.text && !selectedUser && (
          <div className={`mt-4 rounded-lg p-4 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' 
              : message.type === 'error' ? 'bg-red-50 border border-red-200'
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <p className={`text-sm ${
              message.type === 'success' ? 'text-green-800' 
                : message.type === 'error' ? 'text-red-800' : 'text-blue-800'
            }`}>
              {message.text}
            </p>
          </div>
        )}
      </div>

      {selectedUser && (
        <form onSubmit={handleEnable} className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Enable User Account
                </h3>
                <p className="text-sm text-green-800 mb-4">
                  You are about to enable this user account in Active Directory. The user will be able to log in again.
                </p>
                
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Username:</span>
                      <p className="text-gray-900 font-mono">{selectedUser.username}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p className="text-gray-900">{selectedUser.email}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Name:</span>
                      <p className="text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Company:</span>
                      <p className="text-gray-900">{selectedUser.company}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-green-900 mb-2">
                    Type <span className="font-mono bg-green-100 px-2 py-0.5 rounded">{selectedUser.username}</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmUsername}
                    onChange={(e) => setConfirmUsername(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter username to confirm"
                    required
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-green-900 mb-2">
                    SR Ticket <span className="text-green-700 text-xs font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={srTicket}
                    onChange={(e) => setSrTicket(e.target.value)}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter SR Ticket"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Execution Status */}
          {executionStatus && loading && (
            <div className="rounded-lg p-4 bg-blue-50 border border-blue-200">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-blue-800">{executionStatus}</p>
              </div>
            </div>
          )}

          {message.text && (
            <div className={`rounded-lg p-4 ${
              message.type === 'success' ? 'bg-green-50 border border-green-200' 
                : message.type === 'error' ? 'bg-red-50 border border-red-200'
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-sm ${
                message.type === 'success' ? 'text-green-800' 
                  : message.type === 'error' ? 'text-red-800' : 'text-blue-800'
              }`}>
                {message.text}
              </p>
              {message.type === 'success' && renderExecutionResults()}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setSelectedUser(null);
                setConfirmUsername('');
                setSrTicket('');
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading || confirmUsername !== selectedUser?.username}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Enabling...' : 'Enable User'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default UserEnableForm;
